import React, { useState } from "react";

// Importing bootstrap components
import { Col, Card, Form, Button, Spinner } from "react-bootstrap";

// Importing formik & yup
import { Formik } from "formik";
import * as Yup from "yup";

// Importing toast utilities
import { showSuccessToast } from "utils/toast";

import { useDispatch, useSelector } from "react-redux";

import {
  addShift as addShiftInStore,
  updateShift as updateShiftInStore,
  deleteShift as deleteShiftFromStore,
} from "state/slices/shift/shiftSlice";

// Importing shift mutations
import {
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
} from "state/slices/shift/shiftApiSlice";

// Importing user slices
import { updateUserInfo } from "state/slices/auth/authSlice";

// Import Utilities
import { isUserOwnTheData } from "utils/authentication";

const initialValues = {
  id: null,
  name: "",
  startTime: "",
  endTime: "",
  isDefault: "",
};

const GeneralAccessManagement = React.memo(
  ({
    isAllowedToRead,
    isAllowedToCreate,
    isAllowedToUpdate,
    isAllowedToDelete,
    ShiftViewManagement,
  }) => {
    const [selectedRow, setSelectedRow] = useState(null);
    const [formikValues, setFormikValues] = useState(initialValues);

    const dispatch = useDispatch();

    const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

    const [createShift] = useCreateShiftMutation();
    const [updateShift] = useUpdateShiftMutation();
    const [deleteShift] = useDeleteShiftMutation();

    const handleCheckboxChange = (event, row) => {
      if (event.target.checked) {
        setSelectedRow(row);
      } else {
        setSelectedRow(null);
        setFormikValues(initialValues);
      }
    };

    const handleEdit = (row) => {
      setFormikValues(row);
    };

    const addShift = async (shiftData) => {
      try {
        const data = await createShift(shiftData).unwrap();

        dispatch(addShiftInStore(data));

        showSuccessToast("Shift added successfully");
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const updateShiftManage = async (shiftData) => {
      try {
        const data = await updateShift(shiftData).unwrap();

        const updatedShiftData = {
          ...data.shift,
        };

        // Update Shift in client side
        dispatch(updateShiftInStore(updatedShiftData));

        // Update Shift of current user
        if (isUserOwnTheData(loggedUserInfo, "shift", data?.shift.id)) {
          dispatch(
            updateUserInfo({
              field: "shift",
              data: data.shift,
            })
          );
        }

        showSuccessToast("Shift updated successfully");
      } catch (err) {
        console.log(err);
        // Error toast is automatically shown by baseQuery
      }
    };

    const handleSubmit = async (values, { resetForm }) => {
      let { isDefault, id, ...restValues } = values;

      isDefault = (isDefault === "true" || isDefault) === true ? true : false;

      if (id) {
        const updatedValues = {
          id,
          isDefault,
          ...restValues,
        };

        updateShiftManage(updatedValues);

        // Rest Form Data
        setFormikValues(initialValues);
      } else {
        addShift(restValues);
      }

      if (selectedRow?.id) {
        setSelectedRow(null);
      }

      resetForm();
    };

    const deleteShiftManage = async () => {
      try {
        await deleteShift(selectedRow.id).unwrap();

        // Delete shift from localShifts
        dispatch(deleteShiftFromStore(selectedRow.id));

        showSuccessToast("Shift deleted successfully");

        if (selectedRow.id) {
          setFormikValues(initialValues);
          setSelectedRow(null);
        }
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const validationSchema = Yup.object().shape({
      name: Yup.string().required("Shift name is required"),
      startTime: Yup.string().required("Shift start time is required"),
      endTime: Yup.string()
        .required("Shift end time is required")
        .test(
          "endTime",
          "End time must be greater than start time",
          function (value) {
            const { startTime } = this.parent;
            if (startTime && value) {
              return startTime < value;
            }
            return true;
          }
        ),
      isDefault: Yup.boolean().required("Default Shift is required"),
    });
    return (
      <section className="section dashboard">
        {(isAllowedToCreate || isAllowedToUpdate) && (
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize={true}
                initialValues={formikValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, handleSubmit, touched, errors, isSubmitting }) => (
                  <Form noValidate onSubmit={handleSubmit} className="row mt-3">
                    <Col md={3} className="mb-3 mb-md-0">
                      <Form.Group controlId="name">
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Enter shift name"
                          value={values.name}
                          onChange={handleChange}
                          isInvalid={touched.name && !!errors.name}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3 mb-md-0">
                      <Form.Group controlId="startTime">
                        <Form.Control
                          type="time"
                          name="startTime"
                          value={values.startTime}
                          onChange={handleChange}
                          isInvalid={touched.startTime && !!errors.startTime}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.startTime}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3 mb-md-0">
                      <Form.Group controlId="endTime">
                        <Form.Control
                          type="time"
                          name="endTime"
                          value={values.endTime}
                          onChange={handleChange}
                          isInvalid={touched.endTime && !!errors.endTime}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.endTime}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3} className="mb-3 mb-md-0">
                      <Form.Group controlId="isDefault">
                        <Form.Control
                          as="select"
                          name="isDefault"
                          value={values.isDefault.toString()}
                          onChange={handleChange}
                          isInvalid={touched.isDefault && !!errors.isDefault}
                          disabled={isSubmitting}
                        >
                          <option value="">Set as default shift</option>
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                          {errors.isDefault}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Button variant="primary" className="w-100" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                              style={{ width: "18px", height: "18px" }}
                            />
                            {formikValues.id ? "Updating..." : "Adding..."}
                          </>
                        ) : (
                          formikValues.id ? "Save" : "Add"
                        )}
                      </Button>
                    </Col>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        )}
        {isAllowedToRead && (
          <ShiftViewManagement
            isAllowedToUpdate={isAllowedToUpdate}
            isAllowedToDelete={isAllowedToDelete}
            handleCheckboxChange={handleCheckboxChange}
            handleEdit={handleEdit}
            deleteShiftManage={deleteShiftManage}
            selectedRow={selectedRow}
          />
        )}
      </section>
    );
  }
);

export default GeneralAccessManagement;
